import { useEffect, useMemo, useState } from "react"

import { Card } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../Components/Empty"
import SelectComponent from "../../../Components/Select"
import { serviceMap } from "../../../Utils/utils"
import { alertStatusColorMap } from "../../AlertsPage/utils/alertStatusMapping"
import Sankey from "./Sankey"

// serviceMap covers the three alert networks; PayPal is a valid alert_service too, so
// give it a display name rather than leaking the raw enum value.
const SERVICE_LABELS = { ...serviceMap, paypal: "PayPal" }

// Blue gradient ramp across the left of the flow: the root and the networks are
// dimensions, not results, so they stay neutral blue and let the outcome column carry
// the meaning. Endpoints are on-palette and theme-keyed so they stay legible on the
// white and dark cards.
const RAMP = {
  light: ["#072F94", "#225AEA"], // blueDeep -> bluePrimary
  dark: ["#1448CB", "#225AEA"] // blueDepressed -> bluePrimary
}

// Two statuses sit on top of the card they are drawn on: #440F0E is invisible against
// the dark card (1.2:1) and #D0D0D4 against the white one (1.5:1). Step each to its
// neighbouring palette value, in the theme where it fails only, so the bar and its
// ribbons stay readable without drifting off the status hue.
const STATUS_COLOR_BY_THEME = {
  light: { Duplicate: "#A1A1AA" }, // Neutral400
  dark: { "Already a chargeback": "#9E1414" } // redDepressed
}

// Outcome nodes reuse the status colours the alert status tag and the status filter
// already paint, so a green bar here means the same thing as a green tag in the table.
const statusColor = (status, dark) =>
  STATUS_COLOR_BY_THEME[dark ? "dark" : "light"][status] ||
  alertStatusColorMap[status] ||
  alertStatusColorMap["default"]

const lerpHex = (a, b, t) => {
  const ca = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16))
  const cb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16))
  const mix = ca.map((v, i) => Math.round(v + (cb[i] - v) * t))
  return `#${mix.map((v) => v.toString(16).padStart(2, "0")).join("")}`
}

const nodeLabel = (node) =>
  node.kind === "service" ? (SERVICE_LABELS[node.value] ?? node.value) : node.value

// Column index per node kind: the flow always reads all -> source -> outcome. The mobile,
// single-service view drops the "all" column, so its own COLUMN/LAST_COLUMN place the
// service node at the ramp's far (fully saturated) end instead of the desktop's midpoint.
const COLUMN = { all: 0, service: 1, status: 2 }
const LAST_COLUMN = 2
const MOBILE_COLUMN = { service: 0, status: 1 }
const MOBILE_LAST_COLUMN = 1

// Sentinel select value for "show every network at once" - not a real node id, so it can't
// collide with one.
const ALL_ALERTS_OPTION = "__all__"

// Map the { nodes:[{id,kind,value}], links:[{source:id,target:id,value}] } flow onto the
// Sankey contract: nodes { name, color, ... }, links indexed into nodes. Outcome nodes
// take their status colour; the columns before them are sampled from the theme ramp,
// so each ribbon blends the neutral blue of a network into the colour of the outcome
// it lands on.
// `pctOverrides` (node id -> 0-100) lets a caller pin a specific node's "% of total" label
// instead of letting Sankey.js derive it from this chart's own root - the mobile single-
// service view needs this because its "root" (the selected network) is only 100% of
// itself, not of every network, so its own share of the real total has to be computed
// against the unfiltered flow and threaded in here.
const toChartData = (
  flow,
  t,
  ramp,
  dark,
  column = COLUMN,
  lastColumn = LAST_COLUMN,
  pctOverrides = {}
) => {
  const nodes = flow?.nodes || []
  const links = flow?.links || []
  const indexOf = new Map(nodes.map((n, i) => [n.id, i]))
  return {
    nodes: nodes.map((n) => ({
      name: t(nodeLabel(n)),
      color:
        n.kind === "status"
          ? statusColor(n.value, dark)
          : lerpHex(ramp[0], ramp[1], (column[n.kind] ?? 0) / lastColumn),
      segment: n.id,
      kind: n.kind,
      raw: n.value,
      volume: n.volume,
      pctOverride: pctOverrides[n.id]
    })),
    links: links
      .filter((l) => indexOf.has(l.source) && indexOf.has(l.target))
      .map((l) => ({
        source: indexOf.get(l.source),
        target: indexOf.get(l.target),
        value: l.value,
        volume: l.volume
      }))
  }
}

// "Alert flow" section: how alerts from each network resolve.
const SankeySection = ({ flow, theme }) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)

  const dark = theme === "dark"
  const hasData = !!flow && Array.isArray(flow.nodes) && flow.nodes.length > 0
  const ramp = dark ? RAMP.dark : RAMP.light

  // Mobile has no room for the "All alerts" root fanning out into every network side by
  // side, so a select isolates one network and only its own outcome breakdown draws by
  // default - "All alerts" stays reachable as its own option, switching back to the full
  // flow on demand.
  const serviceNodes = useMemo(
    () => (hasData ? flow.nodes.filter((n) => n.kind === "service") : []),
    [flow, hasData]
  )
  const allNode = useMemo(
    () => (hasData ? flow.nodes.find((n) => n.kind === "all") : null),
    [flow, hasData]
  )
  const serviceOptions = useMemo(() => {
    const options = serviceNodes.map((n) => ({ label: t(nodeLabel(n)), value: n.id }))
    return allNode
      ? [{ label: t(nodeLabel(allNode)), value: ALL_ALERTS_OPTION }, ...options]
      : options
  }, [serviceNodes, allNode, t])

  const [selectedServiceId, setSelectedServiceId] = useState(null)
  useEffect(() => {
    if (!serviceNodes.length) return
    if (selectedServiceId === ALL_ALERTS_OPTION) return
    if (serviceNodes.some((n) => n.id === selectedServiceId)) return
    // Default to "All alerts" on first load (or whenever the flow changes and drops the
    // previous selection) - a merchant logging in should see the full picture before
    // narrowing to one network, not land pre-filtered to whichever network happens to be
    // busiest. Falls back to the busiest network only in the (data-shape-wise unexpected)
    // case there's no "all" node to select.
    if (allNode) {
      setSelectedServiceId(ALL_ALERTS_OPTION)
      return
    }
    // node.value is the raw service key ("rdr"), not a count - its actual total lives on
    // its own all -> service link, same as the pctOverrides computation below.
    const countFor = (node) =>
      flow.links.find((l) => l.source === allNode?.id && l.target === node.id)?.value ?? 0
    const busiest = [...serviceNodes].sort((a, b) => countFor(b) - countFor(a))[0]
    setSelectedServiceId(busiest.id)
  }, [serviceNodes, selectedServiceId, flow, allNode])

  const chartData = useMemo(() => {
    if (!hasData) return null
    if (!isMobile) return toChartData(flow, t, ramp, dark)

    if (!selectedServiceId) return null
    if (selectedServiceId === ALL_ALERTS_OPTION) {
      // Mobile has no room for the full all -> service -> status fan-out either, so "All
      // alerts" collapses the middle network column away too: status totals are summed
      // across every network, same 2-column shape as picking a single network.
      const statusIds = new Set(flow.nodes.filter((n) => n.kind === "status").map((n) => n.id))
      const totals = new Map()
      flow.links.forEach((l) => {
        if (!statusIds.has(l.target)) return
        const prev = totals.get(l.target) ?? { value: 0, volume: 0 }
        totals.set(l.target, {
          value: prev.value + (l.value ?? 0),
          volume: prev.volume + (l.volume ?? 0)
        })
      })
      const statusNodes = flow.nodes.filter((n) => totals.has(n.id))
      const aggregatedLinks = statusNodes.map((n) => ({
        source: allNode.id,
        target: n.id,
        ...totals.get(n.id)
      }))
      return toChartData(
        { nodes: [allNode, ...statusNodes], links: aggregatedLinks },
        t,
        ramp,
        dark,
        MOBILE_COLUMN,
        MOBILE_LAST_COLUMN
      )
    }

    const serviceLinks = flow.links.filter((l) => l.source === selectedServiceId)
    const statusIds = new Set(serviceLinks.map((l) => l.target))
    const nodes = flow.nodes.filter((n) => n.id === selectedServiceId || statusIds.has(n.id))
    // The selected network is this filtered chart's own root, so left alone it would read
    // 100% of itself. Its meaningful "% of total" is its share of every network combined -
    // pin that in directly rather than letting Sankey.js derive it from this subset.
    //
    // node.value is a display label here ("All alerts" / the raw service key, e.g. "rdr"),
    // not a count - the real numbers live on the "all" node's own outgoing links, so both
    // totals have to come from there instead.
    const grandTotal = allNode
      ? flow.links
          .filter((l) => l.source === allNode.id)
          .reduce((sum, l) => sum + (l.value ?? 0), 0)
      : 0
    const serviceTotal = allNode
      ? (flow.links.find((l) => l.source === allNode.id && l.target === selectedServiceId)?.value ??
        0)
      : 0
    const pctOverrides =
      grandTotal > 0 ? { [selectedServiceId]: Math.round((serviceTotal / grandTotal) * 100) } : {}
    return toChartData(
      { nodes, links: serviceLinks },
      t,
      ramp,
      dark,
      MOBILE_COLUMN,
      MOBILE_LAST_COLUMN,
      pctOverrides
    )
  }, [flow, hasData, isMobile, selectedServiceId, allNode, ramp, dark, t])

  return (
    <Card
      title={t("Alerts flow")}
      className="card-chart-wrapper withTitle"
      styles={{ header: { border: "none" } }}
    >
      {hasData ? (
        <>
          {isMobile && (
            <SelectComponent
              size="large"
              value={selectedServiceId}
              onChange={setSelectedServiceId}
              options={serviceOptions}
              style={{ width: "100%", marginBottom: 16 }}
            />
          )}
          {chartData && (
            <Sankey data={chartData} theme={theme} unit={t("alerts")} isMobile={isMobile} />
          )}
        </>
      ) : (
        <EmptyComponent type="no_data_available" />
      )}
    </Card>
  )
}

export default SankeySection
